<template>
  <div class="item-transactions">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getItemTransactions"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getItemTransactionCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h1 class="page-title">{{ $t('item_transactions') }}</h1>
          </div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="item.number"
        header="#"
        sortable
        :headerStyle="{ width: '40px' }"
        :bodyStyle="'text-align: center;width: 40px'"
        filterField="itemNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.item && slotProps.data.item.number !== null">
            {{ slotProps.data.item.number.toString().padStart(5, '0') }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="item.description"
        :header="$t('item_description')"
        sortable
        filterMatchMode="contains"
        filterField="itemDescription"
      >
        <template #body="slotProps">
          <span
            v-if="slotProps.data && slotProps.data.item && slotProps.data.item.description !== null"
          >
            {{ slotProps.data.item.description }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="quantity"
        :header="$t('quantity')"
        sortable
        :headerStyle="{ width: '100px' }"
        :bodyStyle="'text-align: center;width: 100px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.quantity !== null">
            {{ slotProps.data.quantity }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            placeholder=">="
          />
        </template>
      </Column>

      <Column
        field="type"
        :header="$t('transaction_type')"
        sortable
        :headerStyle="{ width: '150px' }"
        :bodyStyle="'text-align: center;width: 150px'"
        filterField="type"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.type !== null">
            {{ $t(slotProps.data.type) }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="fromLocation"
        :header="$t('from_location')"
        sortable
        filterMatchMode="contains"
        filterField="fromLocationName"
      >
        <template #body="slotProps">
          <div v-if="slotProps.data">
            <span v-if="slotProps.data.fromProject">
              <i class="pi pi-home mr-2"></i>
              #{{ slotProps.data.fromProject.number }}
              {{ slotProps.data.fromProject.customer.firstname }}
              {{ slotProps.data.fromProject.customer.lastname }}
            </span>
            <span v-if="slotProps.data.fromStorage">
              <i
                v-if="slotProps.data.fromStorage.itemStorageType === 'STATIONARY'"
                class="pi pi-box mr-2"
              ></i>
              <i
                v-if="slotProps.data.fromStorage.itemStorageType === 'VEHICLE'"
                class="pi pi-car mr-2"
              ></i>
              #{{ slotProps.data.fromStorage.number }} {{ slotProps.data.fromStorage.name }}
            </span>
          </div>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="toLocation"
        :header="$t('to_location')"
        sortable
        filterMatchMode="contains"
        filterField="toLocationName"
      >
        <template #body="slotProps">
          <div v-if="slotProps.data">
            <span v-if="slotProps.data.toProject">
              <i class="pi pi-home mr-2"></i>
              #{{ slotProps.data.toProject.number }}
              {{ slotProps.data.toProject.customer.firstname }}
              {{ slotProps.data.toProject.customer.lastname }}
            </span>
            <span v-if="slotProps.data.toStorage">
              <i
                v-if="slotProps.data.toStorage.itemStorageType === 'STATIONARY'"
                class="pi pi-box mr-2"
              ></i>
              <i
                v-if="slotProps.data.toStorage.itemStorageType === 'VEHICLE'"
                class="pi pi-car mr-2"
              ></i>
              #{{ slotProps.data.toStorage.number }} {{ slotProps.data.toStorage.name }}
            </span>
          </div>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="createdBy" :header="$t('created_by')" sortable filterField="createdBySearch">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdBy">
            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}<br />
            {{ slotProps.data.createdBy.email }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column field="createdAt" :header="$t('date')" sortable filterField="createdAt">
        <template #body="slotProps">
          <span v-if="slotProps.data && slotProps.data.createdAt !== null">
            {{ slotProps.data.createdAt }}
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

export default {
  name: 'ItemTransactionsPage',
  components: {
    Breadcrumbs,
    DataTable,
    InputText,
    Column,
  },
  data() {
    return {
      itemTransactionTableStateConfig: {
        name: 'itemTransactions',
        filters: {
          itemNumber: { value: null },
          itemDescription: { value: null },
          type: { value: null },
          fromLocationName: { value: null },
          toLocationName: { value: null },
          quantity: { value: null },
          createdBySearch: { value: null },
          createdAt: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'createdAt', sortOrder: -1 },
      },
    };
  },
  computed: {
    ...mapGetters(['getItemTransactions', 'getItemTransactionCount', 'isLoading', 'getTableState']),
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('item_transactions') }];
    },
    tableStateStore() {
      return this.getTableState(this.itemTransactionTableStateConfig.name);
    },
  },
  methods: {
    ...mapActions(['fetchItemTransactionsPaginated', 'initTableState']),

    /**
     * Load remote table data
     */
    async loadTableData() {
      await this.fetchItemTransactionsPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Remove all filters
     * @param {*} event
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },
  },
  async created() {
    await this.initTableState(this.itemTransactionTableStateConfig);
  },
  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 75vh;
}
</style>
